<template>
    <div class="ms" :class="{ 'ms--compact': compact, 'ms--words-wide': previewWide }">
        <div class="ms-preview" :class="{ 'ms-preview--words': showWords }">
            <!-- ⚠ 縮圖的算繪邏輯集中在 MediaThumb（2026-09-07 抽出）。
                 這裡打開兩個選項，維持既有外觀：
                 - `allowEmbed`：沒有 poster 的 PDF 退回內嵌算繪第一頁。
                   ⚠ **只有「一份素材」的欄位可以開**——`<embed>` 會下載整個 PDF
                   （最多 20MB），所以挑選器與清單絕對不能開。
                 - `showWords` + `maxWords`：字表沒有檔案可看，只能列單字；
                   上限是兩段的（框裝不下才往右長，見 WORDS_PREVIEW_MAX）。
                 ⚠ **`showTargets` 刻意不開**：找不同圖在這裡當一般圖片顯示，
                 熱區框由 `QuestModeSettings` 底下那一塊獨立畫（那裡才有空間）。
                 ⚠ `v-else` 必須緊接在後面，中間不能插任何元素——見下面那一段的說明。 -->
            <MediaThumb v-if="media" :media="media" fit="cover"
                :show-words="showWords" :max-words="wordsCap" allow-embed
                :icon-size="compact ? 22 : 28" />

            <div v-else class="ms-none">
                <v-icon :size="compact ? 20 : 26" color="#cfd8dc">mdi-image-plus-outline</v-icon>
            </div>
        </div>

        <div class="ms-name-wrap">
            <!-- ⚠ 檔名一律顯示。後端的 UploadResult 原本沒有回 `title`，症狀是
                 「上傳完看得到縮圖但沒有名字，存檔重讀之後才出現」（2026-08-28 修）。 -->
            <div v-if="media" class="ms-name">{{ media.title || $t('unit.settings.untitled') }}</div>
            <div v-else class="ms-ph">{{ placeholder }}</div>

            <!-- ⚠ **已下架的素材要標出來**（規格 §7.2.1）。它挑不到了（§2.3 過濾掉），
                 但既然這一題已經在用，就必須顯示得出來並提示要換掉——
                 否則老師只看到一個正常的名字，不知道學生那邊已經壞了。 -->
            <!-- ⚠ **影片要標出是 YouTube 還是自行上傳。** 兩者對老師的意義不同：
                 YouTube 不佔容量配額、不用等轉檔，但依賴那支影片還在線上；上傳的相反。
                 不能讓他只憑「有沒有縮圖」猜——那是巧合，目前只有上傳的影片會抽縮圖。 -->
            <div v-if="provider" class="ms-meta">
                {{ $t(`media.provider.${provider}`) }}
            </div>

            <!-- ⚠ 平台下架與自己下架要講不同的話：自己下架的可以去「我的素材」上架回來，
                 平台下架的**不能**，他只能換素材——而且要看得到原因才知道怎麼換。 -->
            <div v-if="isBanned" class="ms-bad">
                {{ $t('unit.settings.media_banned') }}
                <span v-if="banReason" class="ms-bad-why">{{ banReason }}</span>
            </div>
            <div v-else-if="isDelisted" class="ms-bad">
                {{ $t('unit.settings.media_delisted') }}
            </div>
            <!-- ⚠ **只有 `failed` 要講。** 它是永久的、會擋存檔。
                 `pending` / `processing` 刻意**不在這裡提示**：存檔已經不受它影響
                 （2026-08-31 放寬），轉完自然就好了，所以那個提示只是在解釋一個
                 不存在的限制。轉檔狀態在「我的素材」頁與挑選器都還看得到。 -->
            <div v-else-if="isFailed" class="ms-bad">
                {{ $t('unit.settings.media_failed') }}
            </div>
        </div>

            <!-- ⚠ **一行**，破壞性的動作收進 ⋯（2026-08-31 依設計圖改）。
                 這之前是兩行（上「開啟／移除」下「換一個／上傳新的」），理由是
                 「移除」與「換一個」擺在一起容易誤按。設計圖改用另一種方式解那個問題
                 ——把「移除」收進 ⋯ 選單，需要多一次點擊才碰得到，同時整列只佔一行。 -->
            <div v-if="media" class="ms-acts">
                <!-- 縮圖看不清楚內容，所以給一個真的打得開的入口。
                     影片與 PDF 最需要——老師要確認選到的是不是對的那一份。 -->
                <!-- ⚠ **熱區圖走站內預覽，不開新分頁**（2026-09-04）：新分頁開的是原始
                     圖檔，那上面**看不到任何熱區**——而熱區正是老師要確認的東西。
                     其他 kind 的原始檔就是內容，新分頁是對的。 -->
                <button v-if="isHotspot && media.url" type="button" class="ms-btn"
                    @click="previewOpen = true">
                    {{ $t('unit.settings.open') }}
                </button>
                <a v-else-if="canOpen" :href="media.url" target="_blank" rel="noopener"
                    class="ms-btn">{{ $t('unit.settings.open') }}</a>
                <button type="button" class="ms-btn" @click="$emit('pick')">
                    {{ changeText }}
                </button>

                <!-- ⚠ 用 v-menu 而不是自己 absolute 定位的下拉：它處理了點擊外部關閉、
                     z-index 與貼近視窗邊緣時的翻轉。repo 既有的 ⋯ 選單（Post.vue）也是這樣。 -->
                <v-menu offset-y bottom left>
                    <template #activator="{ on, attrs }">
                        <button type="button" class="ms-btn ms-more" v-bind="attrs" v-on="on">⋯</button>
                    </template>
                    <div class="ms-menu">
                        <!-- ⚠ **只有自己的素材才給編輯入口。** 後端對官方素材與別人的素材
                             一律回 403（`DiaryMediaService.update`），顯示出來只會讓老師
                             按了拿到權限錯誤。 -->
                        <button v-if="canEdit" type="button" class="ms-menu-item"
                            @click="$emit('edit')">
                            {{ $t('media.library.edit_title') }}
                        </button>
                        <!-- ⚠ **只有字表有這一項**：它是唯一內容可以就地改變的素材
                             （其他素材的內容是檔案，換檔案＝換一筆素材）。
                             ⚠ 同樣受 `canEdit` 管——後端對別人的字表一樣回 403。 -->
                        <button v-if="canEdit && isWordlist" type="button" class="ms-menu-item"
                            @click="$emit('edit-words')">
                            {{ $t('media.wordlist.edit_words') }}
                        </button>
                        <!-- ⚠ **只有找不同圖有這一項**（2026-09-04）。熱區屬於素材而不是
                             題目（ADR 0004），所以改的是那份素材——**每一道用到它的題目
                             都會跟著變**。⚠ 但那不會讓任何題目變成不合法（不像字表），
                             所以不需要二次確認。
                             ⚠ 同樣受 `canEdit` 管：官方素材與別人的素材後端回 403。 -->
                        <button v-if="canEdit && isHotspot" type="button" class="ms-menu-item"
                            @click="$emit('edit-hotspot')">
                            {{ $t('media.hotspot.edit_action') }}
                        </button>
                        <button type="button" class="ms-menu-item ms-menu-item--danger"
                            @click="$emit('clear')">
                            {{ $t('unit.settings.remove') }}
                        </button>
                    </div>
                </v-menu>
            </div>

            <!-- 還沒選：一顆按鈕。
                 ⚠ **上傳與「使用 YouTube 影片」的入口在挑選器裡面**（2026-08-31 搬過去），
                 不在這裡——同一個動作放兩個入口只會讓人猜哪一個才對。

                 ⚠⚠ **`v-else` 必須緊接在 `v-if="media"` 的 `.ms-acts` 後面，中間不能插
                 任何元素。** Vue 2 是把 `v-else` 配給**緊鄰前一個**帶 `v-if` /
                 `v-else-if` 的兄弟節點——2026-09-06 曾經在這兩者之間插了
                 `<HotspotPreviewDialog v-if="isHotspot">`，於是這個 `v-else` 改配給了
                 **那一個**：非熱區的素材欄變成 `.ms-acts` 與「選擇素材」**同時渲染**，
                 兩顆按鈕疊在一起、而且上面那顆吃掉了點擊。
                 ⚠ **不會有編譯錯誤也不會有警告**，只有畫面壞掉——所以要在這裡記著。 -->
            <div v-else class="ms-acts">
                <button type="button" class="ms-btn ms-btn--grow ms-btn--primary"
                    @click="$emit('pick')">
                    {{ pickText }}
                </button>
            </div>

            <!-- ⚠ 唯讀的預覽對話框由這個元件自己持有：它沒有 API 呼叫也沒有副作用，
                 往上層拉一個 `preview` 事件只會讓每一個用到 MediaSlot 的父層都要接。
                 ⚠ **一定要放在上面那條 `v-if` / `v-else` 鏈的外面**（見上面的說明）。
                 對話框本身是 portal 算繪的，位置在哪都不影響外觀。 -->
            <HotspotPreviewDialog v-if="isHotspot" :show.sync="previewOpen" :media="media" />

    </div>
</template>

<script>
import HotspotPreviewDialog from '@/components/console/diary/media/HotspotPreviewDialog';
import MediaThumb from '@/components/console/diary/media/MediaThumb';
import {
    providerLabelKey, wordsOf, isBlocked, isBanned, banReasonOf,
} from '@/components/console/diary/media/mediaConstants';

/**
 * 預覽框裡最多列幾個字。**兩段**，因為框本身有兩種寬度。
 *
 * ⚠ 這是**呈現**的上限，不是資料的上限（字表最多 60 個字）。超出的用「還有 N 個」
 * 表示，完整清單在 ⋯ →「編輯單字」。
 *
 * ⚠ **為什麼要兩段而不是一律撐滿。** 字表欄右邊本來就有很多剩餘空間（檔名與兩顆按鈕
 * 用不到 620px），但**一律把預覽框撐滿會讓 3 個字的字表看起來像壞掉的**——一個 450px
 * 寬的框裡只有三顆小 chip。所以規則是「**只有裝不下才長**」：
 *
 * | 字數 | 框 | 顯示 |
 * |---|---|---|
 * | ≤ 8 | 固定 200px（目前的比例） | 全部 |
 * | > 8 | 往右長到吃掉剩餘空間 | 最多 18 個 + 「還有 N 個」 |
 *
 * ⚠ 兩個數字都是配著框的尺寸手調的（200×78 約 3 排 × 3；撐滿後約 3 排 × 6–7）。
 * 改 `.ms-preview--words` 的寬度或 `.ms-word` 的字級，要一起重估這兩個數字。
 */
const WORDS_PREVIEW_MAX = 8;
const WORDS_PREVIEW_MAX_WIDE = 18;

/**
 * 「已選的一份素材」欄位。挑選器與上傳對話框由呼叫端持有——一個題目最多 40 個這種欄位，
 * 每個都掛自己的對話框不可行（見 QuestModeSettings 的 openPicker）。
 *
 * ⚠ 這個元件**只負責呈現與發事件**，不碰 API、不碰設定物件。
 */
export default {
    name: 'MediaSlot',
    components: { HotspotPreviewDialog, MediaThumb },
    props: {
        /** 已選素材（後端題目詳情的 `media` 對照表裡的一筆）。null = 還沒選。 */
        media: { type: Object, default: null },
        placeholder: { type: String, default: '' },
        /** 卡片那種一行放兩個的地方用 compact。 */
        compact: { type: Boolean, default: false },
        /**
         * 覆寫「選擇素材」的文案。
         * ⚠ 給題目封面欄用：它有自己更具體的既有文案（「從素材庫選」），
         * 不必為了共用元件而換成比較籠統的「選擇素材」。
         */
        pickLabel: { type: String, default: '' },
    },
    data() {
        return { previewOpen: false };
    },
    computed: {
        pickText() {
            return this.pickLabel || this.$t('unit.settings.pick');
        },
        changeText() {
            return this.$t('unit.settings.change');
        },
        /**
         * 是不是字表。⚠ 用 `kind` 而不是 `purpose`：字表只有 wordsearch / colorword
         * 兩個 purpose，但**未來會有更多**（規格 §2.5）——用 kind 判斷，
         * 不認識的新字表 purpose 一樣拿得到「編輯單字」。
         */
        isWordlist() {
            return !!(this.media && this.media.kind === 'wordlist');
        },
        /**
         * 是不是找不同圖。⚠ 用 `purpose` 而不是 `kind`——`kind` 是 `image`，
         * 跟封面圖與翻卡牌用圖分不開（那正是 `purpose` 存在的理由，ADR 0004）。
         */
        isHotspot() {
            return !!(this.media && this.media.purpose === 'hotspot');
        },
        /**
         * 預覽框要不要改成列單字。
         * ⚠ **compact 不行**：那個框只有 64×48，塞不了字。目前也不會發生——
         * compact 用在封面欄與翻卡牌的兩面，都是圖片。這一條是防禦。
         */
        showWords() {
            return this.isWordlist && !this.compact;
        },
        /** 完整的字表內容。⚠ 走 `wordsOf` 才擋得住 MariaDB 的 JSON 回字串。 */
        allWords() {
            return wordsOf(this.media);
        },
        /**
         * 預覽框要不要往右長。**只有 200px 裝不下才長**——見 `WORDS_PREVIEW_MAX` 的表格。
         *
         * ⚠ 判斷用**字數**而不是「量到裝不下為止」：量測要等 DOM、會在字表換掉時閃動，
         * 而且結果隨字長變動——同一份字表在不同題目寬度不一樣，反而更難辨認。
         */
        previewWide() {
            return this.showWords && this.allWords.length > WORDS_PREVIEW_MAX;
        },
        /** 這個寬度下能顯示幾個字。 */
        wordsCap() {
            return this.previewWide ? WORDS_PREVIEW_MAX_WIDE : WORDS_PREVIEW_MAX;
        },
        /**
         * 影片縮圖。⚠ **不看 `url`**：轉檔完成前 `url` 是 NULL，但縮圖在上傳當下就抽好了，
         * 那正是它存在的理由（否則老師要等 30 秒–3 分鐘才看得到自己選了什麼）。
         */
        /**
         * 影片來源標籤的 i18n key。不是影片就是空字串。
         *
         * ⚠ 走 `providerLabelKey` 而不是 `providerOf`：老師可以選到官方素材與別的老師
         * 公開的素材，那兩種講「自行上傳」是錯的。這需要 `owner_type`，
         * `QuestMediaBrief` 於 2026-09-08 為此補上那一欄。
         */
        provider() {
            return providerLabelKey(this.media);
        },
        /**
         * 有沒有「開啟」入口。⚠ 字表沒有——它沒有檔案，內容在 `attributes.words`，
         * `url` 是 NULL，給連結只會開出一個空白分頁。
         * ⚠ 還在轉檔的影片也沒有：`url` 那時是空字串。
         */
        canOpen() {
            return !!(this.media && this.media.url
                && ['image', 'pdf', 'video'].indexOf(this.media.kind) !== -1);
        },
        /** 被平台下架。⚠ 與 `isDelisted` 互斥，文案與可做的事都不同。 */
        isBanned() {
            return isBanned(this.media);
        },
        banReason() {
            return banReasonOf(this.media);
        },
        isDelisted() {
            /// ⚠ `isBlocked` 含 banned，但上面的 `isBanned` 分支會先接手，
            /// 所以這一個實際上只會在「擁有者自己下架」時成立。
            return isBlocked(this.media);
        },
        isFailed() {
            return !!(this.media && this.media.process_status === 'failed');
        },
        /** 能編輯這筆素材的資訊嗎。⚠ 只有擁有者可以，見選單那段註解。 */
        canEdit() {
            return !!(this.media && this.media.is_mine);
        },
    },
};
</script>

<style scoped>
/* 設計圖的盒子：淺灰底 + 細框，把「這一格是一份素材」框起來。
   ⚠ **用 grid areas 而不是 flex**，因為設計圖有兩種結構：
   - 寬的（模式設定區，約 620px）：縮圖在左，檔名與按鈕都在右邊
   - 窄的（題目封面欄，260px）：上排縮圖 + 狀態文字，**下排按鈕佔滿整寬**
   同一份 DOM 靠換 grid-template-areas 就能切換。用 flex 做不到——
   按鈕在 260px 欄裡只剩約 164px，兩顆平分會把文字擠爆。 */
.ms {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    grid-template-areas:
        "preview name"
        "preview acts";
    gap: 6px 14px;
    align-items: center;
    border: 1px solid #e4e6ea;
    background: #fafbfc;
    border-radius: 6px;
    padding: 12px;
    /* ⚠ 不要佔滿整個寬度（設計圖的值）。一個「一份素材」的欄位在 1400px 的螢幕上
       拉成整列寬會讓縮圖與檔名之間空一大片，而右邊那三顆按鈕離縮圖越來越遠。
       compact（封面欄 260px、卡片兩面）本來就比這窄，這條對它沒有作用。 */
    /* ⚠ 這個數字被 `QuestModeSettings.vue` 的 `.qs-grid` 對齊（找單字的兩個難度輸入
       要與上面的素材欄同寬）。**改這裡要一起改那裡**——兩個元件各自 scoped，
       共用不了同一個變數。 */
    max-width: 620px;
}

/* compact（封面欄）：縮圖與狀態文字一排，按鈕自己一排佔滿 */
.ms--compact {
    grid-template-areas:
        "preview name"
        "acts    acts";
    gap: 10px 12px;
    padding: 10px;
    align-items: start;
}

.ms-preview {
    grid-area: preview;
}

.ms-name-wrap {
    grid-area: name;
    min-width: 0;
}

.ms-acts {
    grid-area: acts;
}

.ms-preview {
    flex: 0 0 auto;
    width: 104px;
    height: 78px;
    border: 1px solid #eceff1;
    border-radius: 4px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fafafa;
}

.ms--compact .ms-preview {
    width: 64px;
    height: 48px;
}

/* 字表的預覽框：**加寬**（104px 放不下字），高度不變讓它與其他素材欄對齊。
   ⚠ 圖片／PDF／影片的 104×78 是為了固定比例的縮圖；字表沒有比例可言，
   把它塞進同一個框只是浪費——挑選器裡的字表卡片也是用另一套（`.mp-thumb--free`）。
   ⚠ 200px 之後 name/acts 那一欄還有約 368px（`.ms` 上限 620 − padding 24 − gap 14），
   而字表的動作列只有 [換一個][⋯]（沒有「開啟」——它沒有檔案），夠寬。
   ⚠ 改這個寬度要一起改 `WORDS_PREVIEW_MAX`。 */
.ms-preview--words {
    width: 200px;
    align-items: flex-start;
    justify-content: flex-start;
    padding: 5px;
    background: #fff;
}

/* 裝不下的時候才把預覽框往右長（見 `WORDS_PREVIEW_MAX` 的說明）。
   做法是**把兩欄的角色對調**：預覽變成 `1fr`（吃掉剩餘空間），
   檔名與按鈕那一欄改成依內容決定寬度。

   ⚠ 第二欄用 `minmax(0, 200px)` 而不是 `auto`：
   - `auto` 會讓一個長標題把那一欄撐開、反過來壓縮預覽框（`.ms-name-wrap` 的
     `min-width: 0` 擋不住 grid track 的 content sizing）。
   - `minmax(0, 200px)` 的上界是 200px，超過就讓 `.ms-name` 在 200px 內換行
     （它是 `word-break: break-all`，會斷行而不是出現省略號——所以標題再長也不會
     把這一欄撐開，只會變高）。
   - 而它是**依內容決定的軌道**，所以檔名短的時候不會硬佔 200px——只會是
     [換一個][⋯] 那兩顆的寬度。**兩邊都不浪費**。

   ⚠ 字表的動作列沒有「開啟」（它沒有檔案），所以那一欄實際只需要兩顆按鈕的寬度。 */
.ms--words-wide {
    grid-template-columns: minmax(0, 1fr) minmax(0, 200px);
}

.ms--words-wide .ms-preview--words {
    width: auto;
}








.ms-file,
.ms-none {
    display: flex;
    align-items: center;
    justify-content: center;
}

.ms-name {
    font-size: 13px;
    color: #263238;
    word-break: break-all;
}

.ms-ph {
    font-size: 13px;
    color: #b0bec5;
}

.ms-meta {
    font-size: 11px;
    color: #78909c;
    margin-top: 2px;
}

.ms-bad {
    font-size: 12px;
    color: #d32f2f;
    font-weight: 600;
    margin-top: 2px;
}

/* 平台下架的原因。比警告本身輕一級：警告是「要換掉」，原因是「為什麼」——
   原因可能很長，跟警告同樣的粗紅字會讓整塊變成一片紅。 */
.ms-bad-why {
    display: block;
    font-weight: 400;
    color: #8d6e63;
    word-break: break-all;
}


.ms-acts {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
    /* ⚠ 不要加 margin-top：外層 grid 的 row-gap 已經在管間距，會疊加 */
}

.ms-btn {
    font-size: 13px;
    color: #3c4043;
    background: #fff;
    border: 1px solid #cfd3d8;
    border-radius: 4px;
    /* ⚠ **固定高度而不是 padding**。這一列混了 <a>（開啟）、<button>（換一個）與
       字級不同的 ⋯，三者的行高不一樣，用垂直 padding 算出來的高度就會差幾 px。
       固定高度 + inline-flex 置中之後，字級再怎麼換都對齊。 */
    padding: 0 14px;
    height: 33px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    text-decoration: none;
}

.ms-btn:hover {
    background: #f1f3f4;
    text-decoration: none;
}

/* ⚠ **只有 compact 才平分。** 非 compact 的卡片寬約 620px，`flex: 1 1 0` 會把兩顆
   撐成兩個巨大的按鈕；封面欄只有 260px，那時平分才是對的。 */
.ms-btn--grow {
    flex: 0 0 auto;
}

.ms--compact .ms-btn--grow {
    flex: 1 1 0;
}

/* 未選狀態的主要動作。「上傳新的」維持中性——多數時候老師是要從素材庫挑，
   上傳是次要路徑。 */
.ms-btn--primary {
    border-color: #1a73e8;
    color: #1a73e8;
    font-weight: 500;
}

.ms-btn--primary:hover {
    background: #f0f6fe;
}

/* ⚠ 只改字級與左右內距。**不要再動 padding-y 或 line-height**——
   高度由 .ms-btn 的 height 決定，動了就會又跟旁邊差幾 px。 */
.ms-more {
    padding: 0 12px;
    font-size: 14px;
}

.ms-menu {
    background: #fff;
    border-radius: 6px;
    padding: 4px;
    min-width: 148px;
    display: flex;
    flex-direction: column;
}

.ms-menu-item {
    border: 0;
    background: transparent;
    text-align: left;
    padding: 9px 12px;
    font-size: 13px;
    color: #3c4043;
    cursor: pointer;
    border-radius: 4px;
}

.ms-menu-item:hover {
    background: #f1f3f4;
}

.ms-menu-item--danger {
    color: #d93025;
}

.ms-menu-item--danger:hover {
    background: #fce8e6;
}
</style>
